import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Formik } from 'formik';
import { connect } from 'react-redux';
import { Select, Input } from 'sa-ui';
import * as Yup from 'yup';
import omit from 'lodash/omit';
import { promisify } from 'app/actions';
import Field from '../../app/common/Field';
import * as actions from './editClassActions';
import messages from './editClassMessages';
import * as helpers from './editClassHelpers';
import ModalType from './editClassModalType';
import EditStudentModal from './EditStudentModal';

/* eslint-disable react/jsx-no-bind */

import './EditClassContainer.scss';
import EditClassConfirmationModal from './EditClassConfirmationModal';
import EditStudentConfirmationModal from './EditStudentConfirmationModal';
import StudentCardItem from './StudentCardItem';

const ClassSchema = Yup.object().shape({
  idCSchool: Yup.number().required('errorRequired'),
  idCatalogingSegment: Yup.number().required('errorRequired'),
  idCatalogingLevel: Yup.number().required('errorRequired'),
  period: Yup.string().required('errorRequired'),
  courseName: Yup.string().required('errorRequired'),
});

class EditClassContainer extends Component {
  state = {
    modals: {
      confirmation: false,
      student: false,
      studentConfirmation: false,
    },
    createdIdCourse: null,
    confirmationModalType: ModalType.ADD,
    stydentModalType: ModalType.ADD,
    idStudent: null,
  };

  componentDidMount() {
    this.fetch();
  }

  componentDidUpdate(prevProps) {
    // When adding a class, getInitialValues automatically selects the school if the permission
    // filter reduces the list to a single option. The saga already loads the levels when the API
    // returns only one school; this flow covers the case where the API returns multiple schools,
    // but only one remains available. Comparing with prevProps prevents repeated requests.
    const { idESCourse, schoolOptions, unfilteredSchoolOptions } = this.props;
    const singleSchool = schoolOptions.length === 1 ? schoolOptions[0] : null;
    const previousSingleSchool =
      prevProps.schoolOptions.length === 1 ? prevProps.schoolOptions[0] : null;
    const schoolsWereReloaded =
      prevProps.unfilteredSchoolOptions !== unfilteredSchoolOptions;
    const singleSchoolChanged =
      singleSchool && (!previousSingleSchool || previousSingleSchool.value !== singleSchool.value);

    if (
      isNaN(idESCourse) &&
      unfilteredSchoolOptions.length > 1 &&
      singleSchool &&
      (schoolsWereReloaded || singleSchoolChanged)
    ) {
      this.loadSchoolLevels(singleSchool.value);
    }
  }

  getInitialValues = () => {
    const isAdd = isNaN(this.props.idESCourse);
    if (this.props.schoolOptions.length === 1 && isAdd) {
      return { ...this.props.group, idCSchool: this.props.schoolOptions[0].value };
    }
    return this.props.group;
  };

  fetch = () => {
    this.props.getSchools();
    this.props.getPeriods();
    if (Number(this.props.idESCourse)) {
      this.props.getGroupInformation({ idESCourse: this.props.idESCourse });
    } else {
      this.props.newGroup();
    }
    this.props.getGroups({ frontGroups: true });
  };

  loadSchoolLevels = (idCSchool) => {
    this.props.getLevels({ idCSchool, catalogingType: 'segment' });
  };

  handleSubmit = async (values) => {
    const isEdit = Number(this.props.idESCourse);
    const action = isEdit ? this.props.editGroup : this.props.addGroup;
    const modalType = isEdit ? ModalType.EDIT : ModalType.ADD;
    const payload = {
      ...values,
      name: values.courseName,
      idESCourse: isEdit ? this.props.idESCourse : undefined,
    };
    const response = await action(omit(payload, ['courseName']));
    this.setState({
      modals: {
        ...this.state.modals,
        confirmation: true,
      },
      confirmationModalType: modalType,
      createdIdCourse: !isEdit ? response.data.idCourse : null,
    });
  };

  handleContinue = () => {
    if (this.state.createdIdCourse) {
      this.setState(
        {
          // createdIdCourse: null,
          modals: {
            ...this.state.modals,
            confirmation: false,
          },
        },
        () => {
          this.fetch();
          this.props.history.push(`/groups/${this.state.createdIdCourse}`);
        },
      );
      return;
    }
    this.setState(
      {
        modals: {
          ...this.state.modals,
          confirmation: false,
        },
      },
      () => {
        this.props.history.push('/groups');
      },
    );
  };

  handleRequestAddStudent = async () => {
    await this.props.requestEditStudent({});
    this.setState({
      modals: {
        ...this.state.modals,
        student: true,
      },
      studentModalType: ModalType.ADD,
    });
  };

  handleRequestEditStudent = (student) => {
    this.props.requestEditStudent(student);
    this.setState({
      modals: {
        ...this.state.modals,
        student: true,
      },
      studentModalType: ModalType.EDIT,
    });
  };

  handleRequestViewStudent = (student) => {
    this.props.requestViewStudent(student);
    this.setState({
      modals: {
        ...this.state.modals,
        student: true,
      },
      studentModalType: ModalType.VIEW,
    });
  };

  handleAddStudent = async (student) => {
    await this.props.addStudent({
      ...student,
      idESCourse: this.props.idESCourse,
    });
    this.fetch();
    this.setState({
      modals: {
        ...this.state.modals,
        student: false,
        studentConfirmation: true,
      },
      addedStudent: student,
    });
  };

  handleEditStudent = async (student) => {
    await this.props.editStudent({ ...student, idStudent: student.id });
    this.fetch();
    this.setState({
      modals: {
        ...this.state.modals,
        student: false,
      },
    });
  };

  handleDeleteStudent = async (student) => {
    this.setState({
      idStudent: student.id,
    });
    const idCourse = this.props.idESCourse || null;
    await this.props.deleteStudent({ idStudent: student.id, idCourse });
  };

  handleUndoDeleteStudent = async (student) => {
    const idCourse = this.props.idESCourse || null;
    await this.props.undoDeleteStudent({ idStudent: student.id, idCourse });
    this.fetch();
  };

  handleConfirmDelete = () => {
    this.fetch();
  };

  handleCloseStudent = () => {
    this.setState({
      modals: {
        ...this.state.modals,
        student: false,
      },
    });
  };

  handleCloseStudentConfirmation = () => {
    this.setState({
      modals: {
        ...this.state.modals,
        studentConfirmation: false,
      },
    });
  };

  handleCloseConfirmation = () => {
    this.setState({
      modals: {
        ...this.state.modals,
        confirmation: false,
      },
    });
  };

  getCatalogingLevelLabel = (value) => {
    const classOption = this.props.classOptions.find((l) => l.value === value);
    return classOption ? classOption.label : '';
  };

  getMessageTitle = () => {
    const isReadOnly = !this.props.coordinator;
    const isEdit = !!Number(this.props.idESCourse);
    if (isReadOnly) return messages.viewTitle;
    return isEdit ? messages.editTitle : messages.addTitle;
  };

  render() {
    const isEdit = !!Number(this.props.idESCourse);
    const isReadOnly = !this.props.coordinator;
    const idStudentSelected = this.state.idStudent;

    return (
      <div>
        {!isReadOnly && (
          <EditStudentConfirmationModal
            student={this.state.addedStudent}
            show={this.state.modals.studentConfirmation}
            group={this.props.group}
            type={this.state.studentModalType}
            onClose={this.handleCloseStudentConfirmation}
          />
        )}
        <EditStudentModal
          show={this.state.modals.student}
          type={this.state.studentModalType}
          onAdd={this.handleAddStudent}
          readOnly={isReadOnly}
          onEdit={this.handleEditStudent}
          onClose={this.handleCloseStudent}
          groups={helpers.getGroups(this.props.groups)}
          groupSelected={this.props.idESCourse}
        />
        <EditClassConfirmationModal
          show={this.state.modals.confirmation}
          type={this.state.confirmationModalType}
          onClose={this.handleCloseConfirmation}
          onContinue={this.handleContinue}
        />
        <Formik
          initialValues={this.getInitialValues()}
          enableReinitialize
          validationSchema={isReadOnly ? undefined : ClassSchema}
          onSubmit={this.handleSubmit}
          render={(formProps) => (
            <div>
              <div className="row">
                <div className="col-8">
                  <FormattedMessage {...this.getMessageTitle()}>
                    {(txt) => <h1 className="page-title mb-5">{txt}</h1>}
                  </FormattedMessage>
                </div>
                <div className="col-4">
                  {/* <FormattedMessage {...messages.loadList}>
                  {txt => <a className="btn btn-primary btn-sm btn-block">{txt}</a>}
                </FormattedMessage> */}
                </div>
              </div>
              {/* <pre>{JSON.stringify(formProps.errors, null, 2)}</pre> */}
              <div className="row" id="edit-class">
                <div className="col-sm-3">
                  <div className="change-avatar mb-4">
                    <div className="color-switch-popup">
                      <input id="color1" type="radio" name="avatar-color" />
                      <label htmlFor="color1" style={{ backgroundColor: '#abe0de' }}>
                        <i className="fa fa-check" />
                      </label>
                      <input id="color2" type="radio" name="avatar-color" />
                      <label htmlFor="color2" style={{ backgroundColor: '#d4a1dd' }}>
                        <i className="fa fa-check" />
                      </label>
                      <input id="color3" type="radio" name="avatar-color" />
                      <label htmlFor="color3" style={{ backgroundColor: '#c5e2a9' }}>
                        <i className="fa fa-check" />
                      </label>
                      <input id="color4" type="radio" name="avatar-color" />
                      <label htmlFor="color4" style={{ backgroundColor: '#efd581' }}>
                        <i className="fa fa-check" />
                      </label>
                      <input id="color5" type="radio" name="avatar-color" />
                      <label htmlFor="color5" style={{ backgroundColor: '#9bc4ed' }}>
                        <i className="fa fa-check" />
                      </label>
                    </div>
                    <span>{this.getCatalogingLevelLabel(formProps.values.idCatalogingLevel)}</span>
                  </div>
                </div>
                <div className="col-sm-9">
                  <FormattedMessage {...messages.classData}>
                    {(txt) => (
                      <h2 className="section-title">
                        <i className="pe-7s-tools" />
                        {txt}
                      </h2>
                    )}
                  </FormattedMessage>
                  <form noValidate>
                    {/* <pre>{JSON.stringify(this.props.group || { vazio: true }, null, 2)}</pre> */}
                    <Field
                      component={Select}
                      name="idCSchool"
                      nullable
                      readOnly={isReadOnly || this.props.schoolOptions.length === 1}
                      label={this.props.intl.formatMessage(messages.school)}
                      options={this.props.schoolOptions}
                      onChange={(event) => {
                        const { value, name } = event.target;
                        formProps.setFieldValue(name, value);
                        formProps.setFieldValue('idCatalogingSegment', undefined);
                        formProps.setFieldValue('idCatalogingLevel', undefined);
                        formProps.setFieldValue('period', undefined);
                        this.loadSchoolLevels(value);
                      }}
                    />
                    <div className="row">
                      <div className="col-6">
                        <Field
                          component={Select}
                          readOnly={isReadOnly}
                          label={this.props.intl.formatMessage(messages.level)}
                          disabled={!formProps.values.idCSchool}
                          name="idCatalogingSegment"
                          onChange={() => {
                            const { value, name } = event.target;
                            formProps.setFieldValue(name, value);
                            formProps.setFieldValue('idCatalogingLevel', undefined);
                            formProps.setFieldValue('period', undefined);
                            this.props.getClasses({
                              idCSchool: formProps.values.idCSchool,
                              catalogingType: 'level',
                              idCatalogingSegment: value,
                            });
                          }}
                          options={this.props.levelOptions}
                        />
                      </div>
                      <div className="col-3">
                        <Field
                          readOnly={isReadOnly}
                          disabled={!formProps.values.idCatalogingSegment}
                          component={Select}
                          name="idCatalogingLevel"
                          label={this.props.intl.formatMessage(messages.year)}
                          options={this.props.classOptions}
                        />
                      </div>
                      <div className="col-3">
                        <Field
                          readOnly={isReadOnly}
                          label={this.props.intl.formatMessage(messages.name)}
                          name="courseName"
                        />
                      </div>
                    </div>
                    <Field
                      component={Select}
                      readOnly={isReadOnly}
                      disabled={!formProps.values.idCatalogingSegment}
                      name="period"
                      label={this.props.intl.formatMessage(messages.period)}
                      options={this.props.periodOptions}
                    />
                  </form>
                  {isEdit && !isReadOnly && (
                    <div>
                      <FormattedMessage {...messages.students}>
                        {(txt) => (
                          <h2 className="section-title mt-5">
                            {txt} ({(this.props.group.students || []).length})
                          </h2>
                        )}
                      </FormattedMessage>
                      <button
                        className="card add-new card-list edit-class--add-student"
                        onClick={this.handleRequestAddStudent}
                      >
                        <span>
                          <div className="container">
                            <div className="row align-items-center">
                              <div className="col-2">
                                <div
                                  className="add-icon"
                                  style={{
                                    display: 'inline-block',
                                    verticalAlign: 'sub',
                                    marginBottom: 0,
                                  }}
                                >
                                  <i className="ti-plus" />
                                </div>
                              </div>
                              <FormattedMessage {...messages.addStudent}>
                                {(txt) => <div className="col-7">{txt}</div>}
                              </FormattedMessage>
                            </div>
                          </div>
                        </span>
                      </button>
                    </div>
                  )}
                  {this.props.students.map((student) => (
                    <StudentCardItem
                      readOnly={isReadOnly}
                      student={student}
                      onEdit={this.handleRequestEditStudent}
                      onView={this.handleRequestViewStudent}
                      onDelete={this.handleDeleteStudent}
                      onUndoDelete={this.handleUndoDeleteStudent}
                      onConfirmDelete={this.handleConfirmDelete}
                      errorDeleteStudent={this.props.errorDeleteStudent}
                      idStudentSelected={this.state.idStudent}
                    />
                  ))}
                  <div className="row mt-4 justify-content-center">
                    <div className="col-6">
                      <FormattedMessage {...(isReadOnly ? messages.back : messages.cancel)}>
                        {(txt) => (
                          <button
                            onClick={() => this.props.history.push('/groups')}
                            className="btn btn-block btn-outline-primary"
                          >
                            {txt}
                          </button>
                        )}
                      </FormattedMessage>
                    </div>
                    {!isReadOnly && (
                      <div className="col-6">
                        <FormattedMessage {...messages.save}>
                          {(txt) => (
                            <button
                              type="submit"
                              className="btn btn-block btn-primary"
                              onClick={formProps.handleSubmit}
                            >
                              {txt}
                            </button>
                          )}
                        </FormattedMessage>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        />
      </div>
    );
  }
}

EditClassContainer.propTypes = {};

const mapStateToProps = ({ editClass, app }, { match }) => {
  const unfilteredSchoolOptions = editClass.schoolOptions;
  const schoolOptions = unfilteredSchoolOptions.filter(
    ({ value }) => app.canEditSchool || (app.hasAccessToDemoSchool && value == 406),
  );

  return {
    coordinator: app.coordinator,
    ...editClass,
    unfilteredSchoolOptions,
    schoolOptions,
    ...match.params,
  };
};

export default compose(
  withRouter,
  injectIntl,
  connect(
    mapStateToProps,
    promisify(actions, { exclude: ['requestEditStudent', 'requestViewStudent'] }),
  ),
)(EditClassContainer);
